'use client'

import { Button } from '@acko/button'
import { trackEvent } from '@acko-tech/building-blocks.ui.acko-analytics'
import { ABHA_ANALYTICS_EVENTS } from '@components/NewProduct/AbhaHeroWidget/abhaHeroAnalytics'
import { AbhaHeroSuccessFeatures } from '@components/NewProduct/AbhaHeroWidget/steps/AbhaHeroSuccessFeatures'
import { AbhaHeroSuccessQrCard } from '@components/NewProduct/AbhaHeroWidget/steps/AbhaHeroSuccessQrCard'
import type {
  AbhaSuccessContentBlock,
  AbhaSuccessPanelData,
} from '@components/NewProduct/AbhaHeroWidget/steps/abhaSuccessRightPanelTypes'
import { getClassNames } from '@helpers/productWidgetHelper'

export type AbhaHeroSuccessContentSectionProps = {
  panelData: AbhaSuccessPanelData
  appDownloadLink: string
  isMobile?: boolean
  onDownloadApp?: () => void
  onDownloadAbhaCard?: () => void
  ctaAnalyticsProps?: Record<string, unknown>
}

const renderContentBlock = (
  block: AbhaSuccessContentBlock,
  panelData: AbhaSuccessPanelData,
  appDownloadLink: string,
  isMobile: boolean,
  blockIndex: number,
  onDownloadApp?: () => void,
  onDownloadAbhaCard?: () => void,
  ctaAnalyticsProps?: Record<string, unknown>
) => {
  switch (block.type) {
    case 'qr_card':
      return (
        <AbhaHeroSuccessQrCard
          key={`qr-${blockIndex}`}
          block={block}
          panelVariant={panelData.panelVariant}
          appDownloadLink={appDownloadLink}
          isMobile={isMobile}
          analyticsProps={ctaAnalyticsProps}
        />
      )

    case 'features':
      return (
        <AbhaHeroSuccessFeatures
          key={`features-${blockIndex}`}
          block={block}
          panelVariant={panelData.panelVariant}
          isMobile={isMobile}
        />
      )

    case 'footer_button':
      if (!isMobile) {
        return null
      }

      return (
        <div key={`footer-${blockIndex}`} className="abha-hero-success-panel__footer">
          <Button
            variant={block.variant ?? 'primary'}
            size="lg"
            fullWidth
            onClick={() => {
              void (async () => {
                if (ctaAnalyticsProps) {
                  const eventProperties = {
                    ...block.ctaProps?.eventProps,
                    ...ctaAnalyticsProps,
                  }

                  if (block.action === 'redirect') {
                    await trackEvent(ABHA_ANALYTICS_EVENTS.TAP_BTN_CTA_WIDGET, eventProperties)
                  } else {
                    void trackEvent(ABHA_ANALYTICS_EVENTS.TAP_BTN_CTA_WIDGET, eventProperties)
                  }
                }

                if (block.action === 'download_abha') {
                  onDownloadAbhaCard?.()
                  return
                }

                if (block.action === 'redirect') {
                  if (block.ctaProps?.redirectionLink) {
                    window.location.assign(block.ctaProps.redirectionLink)
                  }
                  return
                }

                if (onDownloadApp) {
                  onDownloadApp()
                  return
                }
                if (appDownloadLink) {
                  window.location.href = appDownloadLink
                }
              })()
            }}>
            {block.ctaText}
          </Button>
        </div>
      )

    default:
      return null
  }
}

export const AbhaHeroSuccessContentSection = ({
  panelData,
  appDownloadLink,
  isMobile = false,
  onDownloadApp,
  onDownloadAbhaCard,
  ctaAnalyticsProps,
}: AbhaHeroSuccessContentSectionProps) => {
  if (!panelData.content.length) {
    return null
  }

  return (
    <section
      className={getClassNames(
        [
          'abha-hero-success-panel__content',
          isMobile ? 'abha-hero-success-panel__content--mobile' : 'abha-hero-success-panel__content--desktop',
          `abha-hero-success-panel__content--${panelData.panelVariant}`,
        ],
        {}
      )}>
      {panelData.content.map((block, index) =>
        renderContentBlock(
          block,
          panelData,
          appDownloadLink,
          isMobile,
          index,
          onDownloadApp,
          onDownloadAbhaCard,
          ctaAnalyticsProps
        )
      )}
    </section>
  )
}
